import { useMemo } from "react";
import { Combobox, ComboboxButton, ComboboxInput } from "@headlessui/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { HiCheckCircle, HiChevronDown } from "react-icons/hi2";

import { ComboBoxBaseProps, SelectSingleBaseProp } from "@/types/SelectTypes.type";
import Input from "@/components/atoms/input/Input";
import ComboBoxOptions from "@/components/molecules/combobox/ComboBoxOptions";

export type ComboBoxSingleProp<T extends { id: string; name: string }> = SelectSingleBaseProp<T> & ComboBoxBaseProps;

const ComboBoxSingle = <T extends { id: string; name: string }>({
  id,
  query = "",
  setQuery,
  options,
  value,
  onSelect,
  displayValue = (option) => option?.name,
  placeholder,
  showDropdownIcon = false,
  renderOption,
  hasError,
  leftPlaceholder,
  optionsClassName,
  disabled,
  isLoading = false,
  optionsSearchable = true,
  emptyNode,
  allowFreeText = true,
  showOptionsOnFocus = false,
}: ComboBoxSingleProp<T>) => {
  const { t } = useTranslation();

  const displayedPlaceholder = placeholder || t("select.option");

  const filteredOptions = useMemo(() => {
    if (!optionsSearchable) return options;
    if (query === "") return options;
    return options.filter((o) => displayValue(o).toLowerCase().includes(query.toLowerCase()));
  }, [options, optionsSearchable, query, displayValue]);

  return (
    <div>
      <Combobox
        name={id}
        value={value}
        onChange={onSelect}
        disabled={disabled}
        onClose={() => setQuery(query)}
        immediate={showOptionsOnFocus}
      >
        <div className={"relative"}>
          <ComboboxInput
            as={Input}
            hasError={hasError}
            leftPlaceholder={leftPlaceholder}
            className={clsx((showDropdownIcon || value) && "pr-10")}
            displayValue={(selected: T) => displayValue(selected)}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={displayedPlaceholder}
          />
          {showDropdownIcon && (
            <ComboboxButton disabled={disabled} className="absolute inset-y-0 right-0 flex items-center pr-3.5">
              <HiChevronDown className="size-5 text-fg-secondary" />
            </ComboboxButton>
          )}
          {!showDropdownIcon && value && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3.5">
              <HiCheckCircle className="size-5 text-fg-success-primary" />
            </div>
          )}

          <ComboBoxOptions
            allowFreeText={allowFreeText}
            query={query}
            filteredOptions={filteredOptions}
            isLoading={isLoading}
            renderOption={renderOption}
            displayValue={displayValue}
            optionsClassName={optionsClassName}
            emptyNode={emptyNode}
            showOptionsOnFocus={showOptionsOnFocus}
          />
        </div>
      </Combobox>
    </div>
  );
};

export default ComboBoxSingle;
